import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';
import { useFollows, MAX_FOLLOWS } from './hooks/useFollows';
import { getAllDancerNames, searchDancers } from './utils/dataCache';
import { bucketKey, medalFor, ordinal, timeAgo } from './utils/engagementKeys';
import DancerThumbnail from './DancerThumbnail';
import './MyFeed.css';

const FEED_LIMIT = 60;

function FeedItem({ ev }) {
  const dancerLink = <Link to={`/dancer/${encodeURIComponent(ev.dancerName)}`}>{ev.dancerName}</Link>;
  const via = ev.via ? <span className="feed-via">via {ev.via}</span> : null;

  if (ev.type === 'result') {
    return (
      <li className="feed-item">
        <DancerThumbnail dancerName={ev.dancerName} size={44} />
        <div className="feed-body">
          <div className="feed-line">
            <span className="feed-icon">{medalFor(ev.place)}</span>
            {dancerLink} placed <strong>{ordinal(ev.place)}</strong> in {ev.label}
          </div>
          <div className="feed-meta">
            <Link to={`/competition/${encodeURIComponent(ev.competitionId)}`}>{ev.competitionName}</Link>
            {ev.points ? ` · ${ev.points} pts` : ''} · {timeAgo(ev.ts)} {via}
          </div>
        </div>
      </li>
    );
  }

  const gain = ev.fromRank ? ev.fromRank - ev.toRank : null;
  const icon = gain == null ? '⭐' : gain > 0 ? '📈' : '📉';
  return (
    <li className="feed-item">
      <DancerThumbnail dancerName={ev.dancerName} size={44} />
      <div className="feed-body">
        <div className="feed-line">
          <span className="feed-icon">{icon}</span>
          {dancerLink}{' '}
          {gain == null
            ? <>entered the ranking at <strong>#{ev.toRank}</strong></>
            : <>{gain > 0 ? 'moved up' : 'dropped'} {Math.abs(gain)} {Math.abs(gain) === 1 ? 'place' : 'places'} to <strong>#{ev.toRank}</strong></>}
          {' '}in {ev.label}
        </div>
        <div className="feed-meta">{timeAgo(ev.ts)} {via}</div>
      </div>
    </li>
  );
}

export default function MyFeed() {
  const navigate = useNavigate();
  const { dancers, studios, loaded, signedIn, follow, unfollow } = useFollows();
  const [tab, setTab] = useState('feed');
  const [events, setEvents] = useState([]);
  const [feedLoading, setFeedLoading] = useState(false);
  const [filter, setFilter] = useState('all'); // all | result | rank
  const [search, setSearch] = useState('');
  const [namesReady, setNamesReady] = useState(false);
  const [notice, setNotice] = useState(null);

  const followKey = `${dancers.join('|')}#${studios.join('|')}`;

  useEffect(() => {
    if (!loaded || !signedIn) return undefined;
    if (dancers.length + studios.length === 0) {
      setEvents([]);
      return undefined;
    }
    let cancelled = false;
    setFeedLoading(true);
    const targets = [
      ...dancers.map((n) => ({ type: 'dancer', name: n })),
      ...studios.map((n) => ({ type: 'studio', name: n }))
    ];
    Promise.all(
      targets.map((t) =>
        getDoc(doc(db, 'feedBuckets', bucketKey(t.type, t.name)))
          .then((snap) => (snap.exists() ? snap.data().events || [] : []).map((e) => ({
            ...e,
            via: t.type === 'studio' ? t.name : null
          })))
          .catch(() => [])
      )
    ).then((lists) => {
      if (cancelled) return;
      // Same event can arrive through both the dancer and the studio bucket – keep the dancer copy.
      const byId = new Map();
      lists.flat().forEach((e) => {
        const existing = byId.get(e.id);
        if (!existing || (existing.via && !e.via)) byId.set(e.id, e);
      });
      setEvents([...byId.values()].sort((a, b) => b.ts - a.ts).slice(0, FEED_LIMIT));
      setFeedLoading(false);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, signedIn, followKey]);

  useEffect(() => {
    if (tab !== 'following' || namesReady) return;
    getAllDancerNames().then(() => setNamesReady(true)).catch(() => setNamesReady(true));
  }, [tab, namesReady]);

  const results = useMemo(() => {
    if (!namesReady || search.trim().length < 2) return [];
    return searchDancers(search).slice(0, 8);
  }, [search, namesReady]);

  const visible = events.filter((e) => filter === 'all' || e.type === filter);
  const total = dancers.length + studios.length;

  const handleFollow = async (name) => {
    const res = await follow('dancer', name);
    if (!res.ok && res.reason === 'limit') setNotice(`You can follow up to ${MAX_FOLLOWS}.`);
    else setNotice(null);
  };

  if (!loaded) return <div className="feed-page"><p>Loading…</p></div>;

  if (!signedIn) {
    return (
      <div className="feed-page">
        <h2>📰 My Feed</h2>
        <div className="feed-card feed-empty">
          <p>Follow your favourite dancers and studios and see their new results and ranking moves here.</p>
          <button className="feed-primary" onClick={() => navigate('/login')}>Login / Sign Up</button>
        </div>
      </div>
    );
  }

  return (
    <div className="feed-page">
      <div className="feed-head">
        <h2>📰 My Feed</h2>
        <Link to="/notifications" className="feed-bell">🔔 Alerts</Link>
      </div>

      <div className="feed-tabs">
        <button className={tab === 'feed' ? 'active' : ''} onClick={() => setTab('feed')}>Feed</button>
        <button className={tab === 'following' ? 'active' : ''} onClick={() => setTab('following')}>
          Following ({total})
        </button>
      </div>

      {tab === 'feed' && (
        <>
          {total === 0 ? (
            <div className="feed-card feed-empty">
              <p>You aren&apos;t following anyone yet.</p>
              <p>Tap <strong>☆ Follow</strong> on any dancer or studio profile — or find dancers below.</p>
              <button className="feed-primary" onClick={() => setTab('following')}>Find dancers to follow</button>
            </div>
          ) : (
            <>
              <div className="feed-filters">
                {[['all', 'All'], ['result', '🏅 Results'], ['rank', '📈 Rankings']].map(([k, label]) => (
                  <button key={k} className={filter === k ? 'active' : ''} onClick={() => setFilter(k)}>{label}</button>
                ))}
              </div>
              {feedLoading ? (
                <p>Loading your feed…</p>
              ) : visible.length === 0 ? (
                <div className="feed-card feed-empty">
                  <p>Nothing new yet. Results and ranking moves for the people you follow will appear here.</p>
                </div>
              ) : (
                <ul className="feed-list">
                  {visible.map((ev) => <FeedItem key={`${ev.id}-${ev.via || ''}`} ev={ev} />)}
                </ul>
              )}
            </>
          )}
        </>
      )}

      {tab === 'following' && (
        <>
          <div className="feed-card">
            <h3>Find dancers</h3>
            <input
              className="feed-search"
              type="search"
              placeholder="Search a dancer's name…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {notice && <p className="feed-notice">{notice}</p>}
            {search.trim().length >= 2 && namesReady && results.length === 0 && <p className="feed-muted">No dancers found.</p>}
            <ul className="feed-people">
              {results.map((name) => (
                <li key={name}>
                  <DancerThumbnail dancerName={name} size={36} />
                  <Link to={`/dancer/${encodeURIComponent(name)}`}>{name}</Link>
                  {dancers.includes(name)
                    ? <button className="feed-chip on" onClick={() => unfollow('dancer', name)}>★ Following</button>
                    : <button className="feed-chip" onClick={() => handleFollow(name)}>☆ Follow</button>}
                </li>
              ))}
            </ul>
          </div>

          <div className="feed-card">
            <h3>💃 Dancers ({dancers.length})</h3>
            {dancers.length === 0 ? <p className="feed-muted">None yet.</p> : (
              <ul className="feed-people">
                {dancers.map((name) => (
                  <li key={name}>
                    <DancerThumbnail dancerName={name} size={36} />
                    <Link to={`/dancer/${encodeURIComponent(name)}`}>{name}</Link>
                    <button className="feed-chip on" onClick={() => unfollow('dancer', name)}>Unfollow</button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="feed-card">
            <h3>🏛️ Studios ({studios.length})</h3>
            {studios.length === 0 ? <p className="feed-muted">None yet — follow a studio from its profile page.</p> : (
              <ul className="feed-people">
                {studios.map((name) => (
                  <li key={name}>
                    <span className="feed-studio-icon">🏛️</span>
                    <Link to={`/studio/${encodeURIComponent(name)}`}>{name}</Link>
                    <button className="feed-chip on" onClick={() => unfollow('studio', name)}>Unfollow</button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}
